import { service_shortcuts_manager } from '@/services/shortcuts/SERVICE__shortcuts_manager';
import { Object3D, Quaternion, Vector3 } from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { OpenPoseFirstPersonKeyboardController } from './open_pose_first_person_keyboard_controller';

const PI_2 = Math.PI / 2;

const {
  CAMERA_WASD_MOVEMENT_SPEED_UNITS_PER_SECOND,
  CAMERA_WASD_LOOK_SPEED_X,
  CAMERA_WASD_LOOK_SPEED_Y,
  CAMERA_WASD_WHEEL_SPEED,
  CAMERA_WASD_MOUSE_HEIGHT_SPEED,
  CAMERA_WASD_MOUSE_PAN_SPEED,
} = ConstantsOpenPose;

export class OpenPoseFirstPersonControls {
  /*
   * FIELDS
   */
  private readonly input: OpenPoseFirstPersonKeyboardController;

  public enabled: boolean;

  public movement_speed: number;
  public look_speed_x: number;
  public look_speed_y: number;
  public wheel_speed: number;

  public min_y: number;
  public max_y: number;

  public mouse_height_speed: number;
  public mouse_pan_speed: number;

  public pitch: Object3D;
  public yaw: Object3D;

  private _q: Quaternion;
  private _move: Vector3;

  private readonly object3d: Object3D;

  private rmb_down: boolean;
  private lmb_down: boolean;

  private is_rotation_by_mouse_enabled: boolean;

  /*
   * EVENTS
   */

  private on_mouse_move_bound = (e: MouseEvent) => this.mouse_move(e);
  private on_mouse_down_bound = (e: MouseEvent) => this.mouse_down(e);
  private on_mouse_up_bound = (e: MouseEvent) => this.mouse_up(e);
  private on_wheel_bound = (e: WheelEvent) => this.wheel(e);
  private on_contextmenu_bound = (e: Event) => e.preventDefault();

  /*
   * METHODS
   */

  constructor(object3d: Object3D) {
    this.input = new OpenPoseFirstPersonKeyboardController();

    this.enabled = true;

    this.movement_speed = CAMERA_WASD_MOVEMENT_SPEED_UNITS_PER_SECOND;
    this.look_speed_x = CAMERA_WASD_LOOK_SPEED_X;
    this.look_speed_y = CAMERA_WASD_LOOK_SPEED_Y;
    this.wheel_speed = CAMERA_WASD_WHEEL_SPEED;

    this.mouse_height_speed = CAMERA_WASD_MOUSE_HEIGHT_SPEED;
    this.mouse_pan_speed = CAMERA_WASD_MOUSE_PAN_SPEED;

    this._q = new Quaternion();
    this._move = new Vector3();

    this.rmb_down = false;
    this.lmb_down = false;

    this.is_rotation_by_mouse_enabled = false;

    this.min_y = this.mouse_height_speed = Number.NEGATIVE_INFINITY;
    this.max_y = Number.POSITIVE_INFINITY;

    this.object3d = object3d;
    this.pitch = new Object3D();
    this.yaw = new Object3D();
    this.yaw.add(this.pitch);
    this.pitch.add(this.object3d);

    this.add_listeners();
  }

  set_is_rotation_by_mouse_enabled(value: boolean) {
    this.is_rotation_by_mouse_enabled = value;
  }

  set_wheel_speed(value: number) {
    this.wheel_speed = Math.max(0, value);
  }

  get_object(): Object3D {
    return this.yaw;
  }

  //todo: remove object from scene while dispose
  //todo: call dispose when component unmount
  dispose(): void {
    this.remove_listeners();
  }

  add_listeners() {
    document.addEventListener('mousemove', this.on_mouse_move_bound, false);
    document.addEventListener('mousedown', this.on_mouse_down_bound, false);
    document.addEventListener('mouseup', this.on_mouse_up_bound, false);
    document.addEventListener('wheel', this.on_wheel_bound, { passive: false });
    document.addEventListener('contextmenu', this.on_contextmenu_bound, false);

    this.input.listeners_on();

    service_shortcuts_manager.set_is_enabled(false);
  }

  private remove_listeners() {
    document.removeEventListener('mousemove', this.on_mouse_move_bound, false);
    document.removeEventListener('mousedown', this.on_mouse_down_bound, false);
    document.removeEventListener('mouseup', this.on_mouse_up_bound, false);
    document.removeEventListener('wheel', this.on_wheel_bound as any, false);
    document.removeEventListener('contextmenu', this.on_contextmenu_bound, false);

    this.input.listeners_off();

    service_shortcuts_manager.set_is_enabled(true);
  }

  mouse_down(event: MouseEvent) {
    if (!this.enabled) return;
    if (event.button === 0) this.lmb_down = true;
    if (event.button === 2) this.rmb_down = true;
  }

  mouse_up(event: MouseEvent) {
    if (!this.enabled) return;
    if (event.button === 0) this.lmb_down = false;
    if (event.button === 2) this.rmb_down = false;
  }

  mouse_move(event: MouseEvent): void {
    if (!this.enabled) return;
    if (this.is_rotation_by_mouse_enabled) return;

    const moving = this.input.is_movement_in_progress();

    if (this.lmb_down && !this.rmb_down && !moving) return;

    const movement_x = (event.movementX as number) || 0;
    const movement_y = (event.movementY as number) || 0;

    if (this.rmb_down && this.input.shift_pressed) {
      const pan_x = movement_x * this.mouse_pan_speed;
      const pan_y = -movement_y * this.mouse_height_speed;

      this.yaw.translateX(pan_x);

      const yNew = this.yaw.position.y + pan_y;
      this.yaw.position.y = Math.max(this.min_y, Math.min(this.max_y, yNew));

      return;
    }

    if (moving || this.rmb_down) {
      this.yaw.rotation.y -= movement_x * this.look_speed_x * 0.002;
      this.pitch.rotation.x -= movement_y * this.look_speed_y * 0.002;
      this.pitch.rotation.x = Math.max(-PI_2, Math.min(PI_2, this.pitch.rotation.x));
      return;
    }
  }

  wheel(event: WheelEvent) {
    if (!this.enabled) return;
    if (this.is_rotation_by_mouse_enabled) return;
    event.preventDefault();

    this.object3d.getWorldQuaternion(this._q);
    const forward = new Vector3(0, 0, -1).applyQuaternion(this._q).normalize();

    const direction = event.deltaY > 0 ? 1 : -1;
    const amount = this.movement_speed * this.wheel_speed * 0.05 * direction;

    this.yaw.position.addScaledVector(forward, amount);

    this.object3d.position.y = Math.max(this.min_y, Math.min(this.max_y, this.object3d.position.y));
  }

  update(delta_seconds: number): void {
    const dt = Math.max(0, Math.min(delta_seconds, 0.05));
    const step = this.movement_speed * dt;

    this.object3d.getWorldQuaternion(this._q);
    this.input.apply_movement_delta(this._q, this._move);

    if (this._move.lengthSq() > 0) {
      this._move.normalize();
      this.yaw.position.addScaledVector(this._move, step);
    }

    const delta_pitch = this.input.calculate_rotation_pitch(this.look_speed_y);
    const delta_yaw = this.input.calculate_rotation_yaw(this.look_speed_x);

    if (delta_yaw !== 0) this.yaw.rotation.y += delta_yaw;

    if (delta_pitch !== 0) {
      this.pitch.rotation.x += delta_pitch;
      this.pitch.rotation.x = Math.max(-PI_2, Math.min(PI_2, this.pitch.rotation.x));
    }
  }
}
